'use client';

import type { EmblaCarouselType } from 'embla-carousel';
import { useEffect, useState } from 'react';
import { Button } from '@/app/components/button';
import { IconArrowBack } from '@/app/components/icons/ui-actions/icon-arrow-back';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { Image } from '@/app/components/media/image';
import { PaginationDash } from '@/app/components/navigation/pagination';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import { COMPARE_GALLERY_IMAGE_PARAMS, COMPARE_GALLERY_INLINE_DESKTOP_WIDTHS, COMPARE_GALLERY_INLINE_MOBILE_WIDTHS, COMPARE_GALLERY_INLINE_SIZES, COMPARE_GALLERY_MODAL_DESKTOP_WIDTHS, COMPARE_GALLERY_MODAL_MOBILE_WIDTHS, COMPARE_GALLERY_MODAL_SIZES } from '@/app/lcv/components/sections/compare/list/header-cta/gallery/carousel/constants';
import crossfadeStyles from '@/app/lcv/components/sections/compare/list/header-cta/gallery/carousel/crossfade.module.css';
import { PRODUCT_COMPARE_DATA_GA, PRODUCT_COMPARE_DATA_TEST } from '@/app/lcv/components/sections/compare/tracking';
import type { LcvCompareModel } from '@/app/lcv/components/sections/compare/types';
import { getCategoryNameTh } from '@/app/lcv/components/sections/compare/utils';
import { SpartanProductCustomizeBufferedImage } from '@/app/lcv/components/sections/spartan/products/customize/configurator/preview-media/buffered-image';
import { DEFAULT_VEHICLE_IMAGE } from '@/app/lcv/contents/image';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';
import type { LcvModelCategory, LcvModelColor } from '@/types/tis-cms/types';
const navBaseClassName = cn('center-y property-opacity-translate ease-bounce absolute hidden opacity-0 duration-500');
const navAnimateClassName = cn('group-hover:translate-x-0 group-hover:opacity-100');
interface GalleryCarouselProps {
  model?: LcvCompareModel;
  category?: LcvModelCategory;
  selectedColor?: LcvModelColor;
  startIndex?: number;
  isModal?: boolean;
  isImagesReady?: boolean;
  onClickSlide?: () => void;
  onSlideChange?: (index: number) => void;
}
export const GalleryCarousel = ({
  startIndex = 0,
  model,
  category,
  selectedColor,
  isModal = false,
  isImagesReady = true,
  onSlideChange,
  onClickSlide
}: GalleryCarouselProps) => {
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType | null>(null);
  const {
    currentPage,
    onPageChange
  } = usePaginationSlider(emblaApi);
  const {
    currentIndex,
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextSlider(emblaApi);
  const filteredImages = model ? [selectedColor?.front?.url, selectedColor?.rear?.url, model.interior?.url].filter(img => !!img) : [];
  useEffect(() => {
    if (!emblaApi) return;
    safeCall(onSlideChange, currentIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, emblaApi]);
  return <>
      <Sliders emblaOptions={{
      startIndex
    }} rootChildren={!!isImagesReady && <>
              {!!onClickSlide && <div className="absolute inset-0 cursor-pointer" onClick={onClickSlide} />}

              <div className={cn(navBaseClassName, '-translate-x-full', {
        'pointer-events-none': prevBtnDisabled,
        [navAnimateClassName]: !prevBtnDisabled,
        'left-0 lg:block': !isModal,
        'xl:left-10 xl:block': isModal
      })}>
                <Button iconBefore={<IconArrowBack />} size="xs" color="black" {...createDataAttributes({
          test: 'button-prev-slide'
        })} {...createDataGA({
          click: true,
          eventName: PRODUCT_COMPARE_DATA_GA.main,
          text: `${category ? getCategoryNameTh(category) : ''} ${model?.name}`,
          value: 'Image slide prev'
        })} onClick={onPrevButtonClick} />
              </div>
              <div className={cn(navBaseClassName, 'translate-x-full', {
        'pointer-events-none': nextBtnDisabled,
        [navAnimateClassName]: !nextBtnDisabled,
        'right-0 lg:block': !isModal,
        'xl:right-10 xl:block': isModal
      })}>
                <Button iconBefore={<IconArrowForward />} size="xs" color="black" {...createDataAttributes({
          test: 'button-next-slide'
        })} {...createDataGA({
          click: true,
          eventName: PRODUCT_COMPARE_DATA_GA.main,
          text: `${category ? getCategoryNameTh(category) : ''} ${model?.name}`,
          value: 'Image slide next'
        })} onClick={onNextButtonClick} />
              </div>
            </>} className={cn('group relative', {
      'cursor-grab': isModal
    })} containerClassName="w-full gap-x-px" onEmblaInit={setEmblaApi} {...isImagesReady ? createDataGA({
      click: true,
      eventName: PRODUCT_COMPARE_DATA_GA.main,
      text: `${getCategoryNameTh(category ?? {})} ${model?.name}`,
      value: 'Zoom image'
    }) : {}}>
        {isImagesReady ? filteredImages.map((image, index) => <div key={`${model?.key}${index}`} className={cn('aspect-16-9 relative min-w-full flex-none basis-full overflow-hidden', index === currentIndex && crossfadeStyles.fadeOut)} {...createDataAttributes({
        test: `${PRODUCT_COMPARE_DATA_TEST.imageSlide}-${index}`
      })}>
              <SpartanProductCustomizeBufferedImage src={image || DEFAULT_VEHICLE_IMAGE} alt={`${category ? getCategoryNameTh(category) : ''} ${model?.name}`} width={600} height={338} sizes={isModal ? COMPARE_GALLERY_MODAL_SIZES : COMPARE_GALLERY_INLINE_SIZES} mobileWidths={isModal ? COMPARE_GALLERY_MODAL_MOBILE_WIDTHS : COMPARE_GALLERY_INLINE_MOBILE_WIDTHS} desktopWidths={isModal ? COMPARE_GALLERY_MODAL_DESKTOP_WIDTHS : COMPARE_GALLERY_INLINE_DESKTOP_WIDTHS} params={COMPARE_GALLERY_IMAGE_PARAMS} objectFit="cover" fade={false} loading="lazy" decoding={index === currentIndex ? 'sync' : undefined} swapEffect={index === currentIndex ? 'surfaceCrossfade' : 'none'} className={cn('aspect-16-9 w-full', {
          'overflow-hidden rounded-xs': index === 2 && !isModal,
          'overflow-hidden xl:rounded-sm': index === 2 && isModal
        })} />
            </div>) : <div className="aspect-16-9 relative min-w-full flex-none basis-full overflow-hidden" {...createDataAttributes({
        test: `${PRODUCT_COMPARE_DATA_TEST.imageSlide}-empty`
      })}>
            <Image src={DEFAULT_VEHICLE_IMAGE} width={600} height={338} alt="ISUZU" layout="fill" sources={[{
          srcset: DEFAULT_VEHICLE_IMAGE,
          options: {
            width: 384,
            crop: '16:9'
          }
        }]} />
          </div>}
      </Sliders>
      {!isModal && <PaginationDash totalPages={Math.max(filteredImages.length, 1)} currentPage={currentPage} wrapperClassName={cn('justify-center py-3.5', {
      'opacity-0': !isImagesReady
    })} className="bg-gray-200 data-active:bg-red-500" onPageChange={onPageChange} />}
    </>;
};